import { UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { CustomerToCopy } from '../../../modules/orders/copy-order/types/copyOrderModal.types';
import { ListOrdersDTO } from '../../../modules/orders/order.types';
import { teamOrdersRepository } from '../../../modules/orders/repositories/teamOrders.repository';
import { PaginatedResponse } from '../../../types/request.types';
import {
  getFindCustomersToCopyKey,
  getFindOrdersCountKey,
  getFindSimpleOrdersListKey,
  getMaxSubtotalKey,
  getTeamOrdersKey,
} from './orders.keys';
import {
  findCustomersToCopy,
  findOrdersCount,
  findSimpleOrdersList,
  getMaxSubtotal,
} from './orders.repository';
import {
  FindCustomersToCopyParams,
  FindOrdersCountOutput,
  FindOrdersCountParams,
  FindSimpleOrdersListOutput,
  FindSimpleOrdersListParams,
  GetMaxSubtotalOutput,
} from './orders.types';

export const useFindSimpleOrdersList = (
  params: FindSimpleOrdersListParams,
  options?: UseDataQueryOptions,
) =>
  useDataQuery<FindSimpleOrdersListOutput | null>(
    {
      queryKey: getFindSimpleOrdersListKey(params),
      queryFn: async () => {
        const data = await findSimpleOrdersList({ 'order[createdAt]': 'DESC', ...params });
        if (!data.ok || !data.data) {
          return null;
        }
        return data.data;
      },
    },
    options,
  );

export const useFindOrdersCount = (params: FindOrdersCountParams, options?: UseDataQueryOptions) =>
  useDataQuery<FindOrdersCountOutput>(
    {
      queryKey: getFindOrdersCountKey(params),
      queryFn: async () => {
        const response = await findOrdersCount(params);
        if (!response.ok || !response.data) {
          throw response.originalError;
        }
        return response.data;
      },
    },
    options,
  );

export const useGetMaxSubtotal = () => {
  const { data } = useDataQuery<GetMaxSubtotalOutput | null>({
    queryKey: getMaxSubtotalKey(),
    queryFn: async () => {
      const res = await getMaxSubtotal();
      if (!res.ok || !res.data) {
        return null;
      }
      return res.data;
    },
  });

  return {
    data: data ?? {
      maxSubtotal: 0,
    },
  };
};

export const useGetTeamOrders = (options?: UseDataQueryOptions) => {
  const { brandId } = useApplicationContext();

  return useDataQuery<ListOrdersDTO[]>(
    {
      queryKey: getTeamOrdersKey(brandId),
      queryFn: async () => {
        const response = await teamOrdersRepository.getTeamOrders({ brandId });

        if (response.ok && response.data) {
          return response.data;
        }

        throw response.originalError;
      },
    },
    options,
  );
};

export const useFindCustomersToCopy = (
  params: FindCustomersToCopyParams,
  options?: UseDataQueryOptions,
) =>
  useDataQuery<PaginatedResponse<CustomerToCopy>>(
    {
      queryKey: getFindCustomersToCopyKey(params),
      queryFn: async () => {
        const response = await findCustomersToCopy(params);
        if (!response.ok || !response.data) {
          throw response.originalError;
        }
        return response.data;
      },
    },
    options,
  );
